import { useEffect, useState } from "react";
import { ExternalLink, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { MyAffiliate } from "@/hooks/useMyAffiliates";

type Props = {
  affiliate: MyAffiliate;
  onSaved: () => void;
};

const csv = (arr: string[] | null | undefined) => (arr ?? []).join(", ");
const parseCsv = (s: string) =>
  s.split(",").map((v) => v.trim()).filter(Boolean);

const ProfileTab = ({ affiliate, onSaved }: Props) => {
  const [form, setForm] = useState({
    tagline: affiliate.tagline ?? "",
    description: affiliate.description ?? "",
    website: affiliate.website ?? "",
    contact_email: "",
    location: affiliate.location ?? "",
    logo_url: affiliate.logo_url ?? "",
    services: csv(affiliate.services),
    specialties: csv(affiliate.specialties),
    show_contact_email_public: affiliate.show_contact_email_public,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Contact email is column-restricted; fetch via secure RPC for the owner.
    (async () => {
      const { data } = await supabase.rpc(
        "get_my_affiliate_contact_email" as any,
        { _affiliate_id: affiliate.id },
      );
      if (cancelled) return;
      setForm((f) => ({ ...f, contact_email: (data as string | null) ?? "" }));
    })();
    setForm({
      tagline: affiliate.tagline ?? "",
      description: affiliate.description ?? "",
      website: affiliate.website ?? "",
      contact_email: "",
      location: affiliate.location ?? "",
      logo_url: affiliate.logo_url ?? "",
      services: csv(affiliate.services),
      specialties: csv(affiliate.specialties),
      show_contact_email_public: affiliate.show_contact_email_public,
    });
    return () => {
      cancelled = true;
    };
  }, [affiliate.id]);

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("affiliates")
        .update({
          tagline: form.tagline || null,
          description: form.description || null,
          website: form.website || null,
          contact_email: form.contact_email || null,
          location: form.location || null,
          logo_url: form.logo_url || null,
          services: parseCsv(form.services),
          specialties: parseCsv(form.specialties),
          show_contact_email_public: form.show_contact_email_public,
        })
        .eq("id", affiliate.id);
      if (error) throw error;
      toast.success("Profile updated");
      onSaved();
    } catch (e: any) {
      toast.error(e.message ?? "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">{affiliate.name}</h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={affiliate.status === "active" ? "default" : "secondary"}>
              {affiliate.status}
            </Badge>
            {affiliate.featured ? <Badge variant="outline">Featured</Badge> : null}
          </div>
        </div>
        <a
          href={`/affiliates/${affiliate.slug}`}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-primary hover:underline inline-flex items-center gap-1"
        >
          View public profile <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm">Tagline</Label>
          <Input
            className="mt-1"
            value={form.tagline}
            maxLength={160}
            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-sm">Location</Label>
          <Input
            className="mt-1"
            value={form.location}
            placeholder="e.g. Mobile, AL"
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
        </div>
      </div>

      <div>
        <Label className="text-sm">About</Label>
        <Textarea
          className="mt-1"
          rows={6}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm">Website</Label>
          <Input
            className="mt-1"
            value={form.website}
            placeholder="https://"
            onChange={(e) => setForm({ ...form, website: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-sm">Logo URL</Label>
          <Input
            className="mt-1"
            value={form.logo_url}
            placeholder="https://"
            onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
          />
        </div>
      </div>

      <div>
        <Label className="text-sm">Services (comma-separated)</Label>
        <Input
          className="mt-1"
          value={form.services}
          onChange={(e) => setForm({ ...form, services: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-sm">Specialties (comma-separated)</Label>
        <Input
          className="mt-1"
          value={form.specialties}
          onChange={(e) => setForm({ ...form, specialties: e.target.value })}
        />
      </div>

      <div className="rounded-lg border border-border p-4 flex items-start justify-between gap-4">
        <div>
          <Label className="text-sm">Contact email</Label>
          <Input
            className="mt-1"
            type="email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          />
          <p className="text-xs text-muted-foreground mt-2">
            Where new lead requests are sent. Toggle to show it on your public profile too.
          </p>
        </div>
        <div className="flex items-center gap-2 pt-6">
          <Switch
            id="show-email"
            checked={form.show_contact_email_public}
            onCheckedChange={(v) => setForm({ ...form, show_contact_email_public: v })}
          />
          <Label htmlFor="show-email" className="text-sm">Public</Label>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="gap-2">
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

export default ProfileTab;